import React, { useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useTransition, animated } from 'react-spring'
import GatsbyImage from 'gatsby-image'
import loadable from '@loadable/component'
import { decode } from 'shopify-gid'
import { useInView } from 'react-intersection-observer'
import { LayoutContext, useUI } from '../../contexts'
import LineItem from '../LineItem'
import { getCartBundleDiscountAmount } from '../CurrencyConverterSwitcher'
import { Icon } from '../shared/Icon'
import { useCartViewedTracking } from '../../hooks/tracking'
import GiftWrapping from './GiftWrapping'
import { useShopify } from '../../contexts/shopify'
import { useGlobal } from '../../hooks/useGlobal'
import { useFormatPrice } from '../../hooks/useFormatPrice'

const RichText = loadable(() => import('../RichText'))

const Cart = ({ wrapperEl, announcementVisible }) => {
  // -------------------variables----------------------
  // const cartOpen = true // for testing
  // IE doesn't support intersection observer, breaks the site
  const isIE =
    /*@cc_on!@*/ false ||
    (typeof window !== `undefined` && !!document.documentMode)
  const [cartRef, inView] = isIE ? [] : useInView()

  //-------------------CONTEXT---------------------
  const {
    node_locale,
    currency: localCurrency,
    storeSubdomain,
  } = useContext(LayoutContext)

  const {
    cart: {
      cart,
      checkout: checkoutButton,
      shippingCost,
      subtotal: subtotalText,
      discount: discountText,
      cartShippingFeeMessage,
      cartShippingFeeMessageV2,
      cartFreeShippingMessage,
      cartTieredDiscountMessage,
      showTieredDiscountMessage,
      showSpringSaleTieredDiscountMessage,
      cartAddDiscountProductMessage,
      cartDiscountApplied,
      cartEmptyMessage,
      cartEmptyMessageWithDiscount,
      cartPaymentIcons,
    },
    layout: { giftWrappingProduct, showGiftWrappingInCart },
    blackFriday: {
      blackFridayThemeColor,
      showAnnouncementBarBlackFriday2022,
      bFCartDiscountMessage2022,
    },
  } = useGlobal(['cart', 'layout', 'blackFriday'], node_locale)

  const { cartOpen, setCartOpen } = useUI()
  const formatPrice = useFormatPrice()

  //-------------------STATE---------------------

  const [currencyCode, setCurrencyCode] = useState('EUR')
  const [currencySymbol, setCurrencySymbol] = useState('€')
  const [wrapperHeight, setWrapperHeight] = useState(0)

  //-------------------SELECTORS---------------------
  const { checkouts: allCheckouts } = useShopify()
  const checkouts = allCheckouts[storeSubdomain]

  const discountCodeApplication = checkouts.discountApplications?.edges.some(
    ({ node: { __typename } }) => __typename === 'DiscountCodeApplication',
  )

  const hasGiftWrapping = checkouts.lineItems.edges.some(
    ({
      node: {
        variant: { product },
      },
    }) => product.handle === giftWrappingProduct.handle,
  )

  // -------------------currency converter---------------------
  useEffect(() => {
    if (localCurrency) {
      setCurrencyCode(localCurrency.code)
      setCurrencySymbol(localCurrency.symbol)
    }
  }, [localCurrency])

  // --------------announcement bar scroll position business-------------------
  useEffect(() => {
    const handleScroll = () => {
      if (typeof window !== 'undefined' && wrapperEl.current) {
        const currentOffset = window.pageYOffset
        const announcementEl = wrapperEl.current.children[0]
        const announcementHeight = announcementEl.clientHeight
        if (currentOffset < announcementHeight) {
          setWrapperHeight(wrapperEl.current.offsetHeight - currentOffset)
        } else {
          setWrapperHeight(
            wrapperEl.current.offsetHeight -
              parseFloat(wrapperEl.current.dataset.translated),
          )
        }
      }
    }
    cartOpen && handleScroll()
  }, [cartOpen, wrapperEl])

  // -----------------disable scroll and scroll persist when cart is opened------------------
  useEffect(() => {
    const scrollPersistEl = document.getElementById('cart-scroll')
    if (cartOpen && scrollPersistEl) {
      import(/*webpackChunkName: "scroll-lock"*/ 'scroll-lock').then(
        (module) => {
          const { disablePageScroll } = module
          disablePageScroll(scrollPersistEl)
        },
      )
    } else {
      import(/*webpackChunkName: "scroll-lock"*/ 'scroll-lock').then(
        (module) => {
          const { enablePageScroll } = module
          enablePageScroll(scrollPersistEl)
        },
      )
    }
  }, [cartOpen])

  // -----------------bundle discount calculations--------------------
  const amountOfProducts = checkouts.lineItems.edges.reduce(
    (acc, { node: { quantity } }) => {
      return acc + quantity
    },
    0,
  )
  const amountOfEligibleDiscountProducts = checkouts.lineItems.edges.reduce(
    (acc, { node: { quantity, title } }) => {
      // excludes Washing bag and gift wrapping
      return title.toLowerCase().includes('washing') ||
        title.toLowerCase().includes('gift')
        ? acc
        : acc + quantity
    },
    0,
  )

  const priceOfEligibleDiscountProducts = checkouts.lineItems.edges.reduce(
    (acc, { node: { quantity, title, discountAllocations } }) => {
      const totalDiscountsOfProduct = discountAllocations.reduce(
        (acc, item) => {
          if (currencyCode === item.allocatedAmount.currencyCode) {
            acc += Number(item.allocatedAmount.amount)
          }
          return acc
        },
        0,
      )

      // excludes Washing bag and gift wrapping
      return title.toLowerCase().includes('washing') ||
        title.toLowerCase().includes('gift')
        ? acc
        : acc + totalDiscountsOfProduct
    },
    0,
  )
  const shippingDiscount = checkouts.discountApplications?.edges.some(
    ({ node: { targetType } }) => targetType === 'SHIPPING_LINE',
  )
  const discountTotal = checkouts.lineItems.edges
    .filter(
      ({ node: { discountAllocations } }) => discountAllocations.length > 0,
    )
    .map(({ node: { discountAllocations } }) =>
      discountAllocations.reduce(
        (acc, { allocatedAmount: { amount } }) =>
          parseFloat(acc) + parseFloat(amount),
        0,
      ),
    )
    .reduce((acc, discountAmount) => acc + parseFloat(discountAmount), 0)

  const compareDiscount = checkouts.lineItems.edges.reduce(
    (acc, { node: { variant, quantity } }) => {
      const compared =
        variant?.presentmentPrices?.edges[0].node?.compareAtPrice?.amount
      const original = variant?.presentmentPrices?.edges[0].node?.price?.amount

      if (compared) {
        const totalComparedDiscount = compared - original
        acc += totalComparedDiscount * quantity
      }

      return acc
    },
    0,
  )

  const bundleDiscountAmount = getCartBundleDiscountAmount(
    node_locale,
    amountOfEligibleDiscountProducts,
    priceOfEligibleDiscountProducts,
  )

  // ------------------subtotal calculations---------------------
  const subtotal = checkouts.lineItems.edges
    .map(({ node: { variant, quantity } }) => {
      return parseFloat(
        variant?.presentmentPrices?.edges.find(
          ({
            node: {
              price: { currencyCode: currency },
            },
          }) => currency === currencyCode,
        )?.node?.price?.amount * quantity,
        10,
      )
    })
    .reduce((acc, cur) => acc + cur, 0)
  const checkoutTotal =
    showTieredDiscountMessage || showSpringSaleTieredDiscountMessage
      ? subtotal - discountTotal
      : subtotal

  // --------------------tracking-----------------------
  useEffect(() => {
    if (!isIE) {
      if (inView) {
        setTimeout(() => {
          useCartViewedTracking({
            currency: localCurrency?.code,
            value: checkoutTotal.toFixed(1),
            locale: node_locale,
            checkoutId: checkouts?.id && decode(checkouts.id).id,
            numberOfUnits: amountOfProducts,
            numberOfProducts: checkouts?.lineItems?.edges?.length,
            products: checkouts?.lineItems?.edges.map(
              ({
                node: {
                  title,
                  quantity,
                  variant: {
                    id: variantId,
                    presentmentPrices,
                    sku,
                    title: variant,
                    product: { id: productId },
                    image: { transformedSrc },
                  },
                },
              }) => ({
                name: title,
                variant,
                quantity,
                product_id: decode(variantId).id,
                price: presentmentPrices.edges.find(
                  ({
                    node: {
                      price: { currencyCode },
                    },
                  }) => currencyCode === localCurrency?.code,
                ).node.price.amount,
                value:
                  presentmentPrices.edges.find(
                    ({
                      node: {
                        price: { currencyCode },
                      },
                    }) => currencyCode === localCurrency?.code,
                  ).node.price.amount * quantity,
                sku,
                brand: 'Stox',
                category: 'Socks',
                imageUrl: transformedSrc,
              }),
            ),
          })
        }, 1000)
      }
    }
  }, [!isIE && inView])

  // ---------------cart open and close animation--------------------
  const transition = useTransition(cartOpen, null, {
    from: { transform: 'translateX(100%)' },
    enter: {
      transform: 'translateX(0)',
    },
    leave: { transform: 'translateX(100%)' },
    config: { mass: 1, tension: 250, friction: 30 },
  })

  // Show gift wrapping only for EU
  const showGiftWrappingLocales =
    node_locale !== 'en-GB' && node_locale !== 'en-US'

  /**
   * Cart discount message
   */
  const cartDiscountMessage = JSON.parse(cartTieredDiscountMessage?.raw ?? '{}')

  return (
    <>
      <Overlay
        visible={cartOpen}
        onClick={() => setCartOpen(false)}
        title={'Open/Close slide in cart'}
      />
      {transition.map(
        ({ item, key, props }) =>
          item && (
            <StyledCart
              cartOpen={cartOpen}
              style={props}
              key={key}
              wrapperheight={
                announcementVisible
                  ? wrapperHeight
                  : wrapperEl?.current?.offsetHeight -
                    parseFloat(wrapperEl?.current?.dataset?.translated)
              }
            >
              <CartWrapper ref={cartRef}>
                <Header>
                  <CartTitleDesktop>{cart}</CartTitleDesktop>
                  <CloseButton
                    type='button'
                    aria-label='close cart'
                    onClick={() => setCartOpen(false)}
                  />
                </Header>
                <CartScroll id='cart-scroll'>
                  <EmptyCartMessage
                    visible={amountOfProducts <= 0}
                    onClick={() => setCartOpen(!cartOpen)}
                  >
                    <RichText json={cartEmptyMessage} />
                  </EmptyCartMessage>
                  <ProductsList visible={amountOfProducts > 0}>
                    <div>
                      {checkouts.lineItems.edges.map((lineItem, i) => {
                        return (
                          <ProductListItem key={i}>
                            <LineItem lineItem={lineItem} />
                          </ProductListItem>
                        )
                      })}
                    </div>
                  </ProductsList>
                </CartScroll>
                {showAnnouncementBarBlackFriday2022 &&
                  bFCartDiscountMessage2022 && (
                    <BFDiscountMessage>
                      <RichText json={bFCartDiscountMessage2022} />
                    </BFDiscountMessage>
                  )}
                <Footer disabled={subtotal === 0}>
                  {showGiftWrappingInCart &&
                    showGiftWrappingLocales &&
                    giftWrappingProduct &&
                    !hasGiftWrapping && (
                      <GiftWrapping
                        currencyCode={currencyCode}
                        checkoutId={checkouts?.id && decode(checkouts.id).id}
                        cartUnitCount={amountOfEligibleDiscountProducts}
                      />
                    )}
                  <OrderSummary
                    hasTopBorder={!showGiftWrappingInCart || hasGiftWrapping}
                  >
                    <Row>
                      <Key scope='row'>{subtotalText}</Key>
                      <Value>{formatPrice(subtotal)}</Value>
                    </Row>
                    {((discountTotal > 0 && showTieredDiscountMessage) ||
                      (amountOfEligibleDiscountProducts >= 2 &&
                        showSpringSaleTieredDiscountMessage) ||
                      discountCodeApplication ||
                      !!compareDiscount) && (
                      <Row>
                        <Key scope='row'>{discountText}</Key>
                        <DiscountValuesWrapper>
                          {!!compareDiscount &&
                            showAnnouncementBarBlackFriday2022 && (
                              <Value color={blackFridayThemeColor}>
                                {formatPrice(compareDiscount)}
                              </Value>
                            )}
                          {/* HOTFIX for discount displaying incorrectly in the UK store when a percentage */}
                          {/* {!showSpringSaleTieredDiscountMessage &&
                            (showTieredDiscountMessage ||
                              // (showTieredDiscountMessage &&
                              // amountOfEligibleDiscountProducts >= 2) ||
                              (node_locale === 'en-US' &&
                                showAnnouncementBarBlackFriday)) && (
                              <Value
                                blue
                                color={
                                  showAnnouncementBarBlackFriday
                                    ? blackFridayThemeColor
                                    : null
                                }
                              >
                                {node_locale === 'en-US' ? (
                                  <>
                                    {node_locale === 'de' ||
                                    node_locale === 'fr'
                                      ? `${bundleDiscountAmount} ${currencySymbol}`
                                      : `${currencySymbol}${bundleDiscountAmount}`}
                                  </>
                                ) : (
                                  <>
                                    {node_locale === 'de' ||
                                    node_locale === 'fr'
                                      ? `${cartDiscountMessage.content[0].content[0].value} ${amountOfEligibleDiscountProducts} ${cartDiscountMessage.content[1].content[0].value} ${bundleDiscountAmount} ${currencySymbol}`
                                      : `${cartDiscountMessage.content[0].content[0].value} ${amountOfEligibleDiscountProducts} ${cartDiscountMessage.content[1].content[0].value} ${currencySymbol}${bundleDiscountAmount}`}
                                  </>
                                )}
                                {node_locale === 'de' || node_locale === 'fr'
                                  ? `${bundleDiscountAmount} ${currencySymbol}`
                                  : `${currencySymbol}${bundleDiscountAmount}`}
                              </Value>
                            )} */}
                          {amountOfEligibleDiscountProducts >= 2 &&
                            discountCodeApplication && (
                              <Value blue>&nbsp;+&nbsp;</Value>
                            )}
                          {discountCodeApplication && (
                            <Value blue>{cartDiscountApplied}</Value>
                          )}
                          {showSpringSaleTieredDiscountMessage &&
                            amountOfEligibleDiscountProducts >= 2 && (
                              <Value blue>{formatPrice(discountTotal)}</Value>
                            )}
                          {showTieredDiscountMessage &&
                            amountOfEligibleDiscountProducts >= 1 && (
                              <Value color='#ff1e00'>
                                {formatPrice(discountTotal)}
                              </Value>
                            )}
                        </DiscountValuesWrapper>
                      </Row>
                    )}
                    <Row>
                      <Key scope='row'>{shippingCost}</Key>
                      {shippingDiscount || checkoutTotal > 50 ? (
                        <Value>{cartFreeShippingMessage}</Value>
                      ) : (
                        <Value>{cartShippingFeeMessage}</Value>
                      )}
                    </Row>
                  </OrderSummary>

                  <Checkout
                    href={
                      subtotal === 0
                        ? null
                        : checkouts.webUrl + '&locale=' + node_locale
                    }
                  >
                    <Icon name='lock' />
                    <span>{`${checkoutButton} - `}&nbsp;</span>
                    {formatPrice(checkoutTotal)}
                  </Checkout>
                  {node_locale === 'de' && (
                    <GermanText onClick={() => setCartOpen(false)}>
                      Weiter einkaufen
                    </GermanText>
                  )}

                  <PaymentMethods>
                    <GatsbyImage fluid={cartPaymentIcons.fluid} />
                  </PaymentMethods>
                </Footer>
              </CartWrapper>
            </StyledCart>
          ),
      )}
    </>
  )
}

const StyledCart = styled(animated.section)`
  background: white;
  z-index: 99999;
  width: 100vw;
  overflow: hidden;
  position: fixed;
  top: ${(props) => props.wrapperheight}px;
  right: 0;
  bottom: 0;
  display: block;
  visibility: ${({ cartOpen }) => (cartOpen ? 'visible' : 'none')};

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: block;
    width: 35vw;
    top: 0;
    box-shadow: 8px 0 10px 6px rgba(0, 0, 0, 0.05);
  }
`

const Overlay = styled.button`
  display: none;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: block;
    visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
    width: 100vw;
    height: 100vh;
    background: ${(props) => (props.visible ? 'rgba(0, 0, 0, 0.1)' : '0')};
    position: fixed;
    left: 0;
    top: 0;
    z-index: 100;
    transition: visibility 0.3s, background 0.3s ease-out;
  }
`

const CartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding: 0 4.76vw;
  }
`

const CartScroll = styled.div`
  overflow-y: scroll;
  flex: 2 2 auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: ${(props) => props.theme.space.marginMobile}vw;

  &::-webkit-scrollbar {
    display: none;
  }

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    overflow-y: auto;
    padding-bottom: ${(props) => props.theme.space.marginDesktop}vw;
  }
`

const Header = styled.header`
  display: flex;
  justify-content: space-between;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding: 2.65vw 0;
  }
`

const FreeShipping = styled.div`
  overflow: hidden;
  display: ${(props) => (props.desktop ? 'none' : 'block')};
  padding: ${(props) => props.theme.space.marginMobile}vw
    ${(props) => props.theme.space.marginMobile}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: ${(props) => (props.desktop ? 'block' : 'none')};
    width: 100%;
    padding: 0;
  }
`

const FreeShippingMessage = styled.div`
  font-size: ${(props) => props.theme.fontSize.m26};
  padding-bottom: ${(props) => props.theme.space.marginMobile}vw;
  padding-left: 0;

  span {
    font-family: ${(props) => props.theme.fontFamily.bold};
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding-left: 0;
    font-size: ${(props) => props.theme.fontSize.d15};
    padding-bottom: 0.4vw;
  }
`

const FreeShippingBar = styled.div`
  width: calc(100%);
  background: ${(props) => props.theme.colors.mediumGrey};
  height: 3px;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    width: calc(72%);
    height: 0.1388888889vw;
  }

  &::before {
    content: '';
    width: ${(props) => (props.progress <= 0 ? 2 : props.progress)}%;
    max-width: 100%;
    background: ${(props) => props.theme.colors.blue};
    height: 3px;
    display: block;
    transition: width 0.2s;
    transition-delay: 0.4s;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    &::before {
      content: '';
      width: ${(props) => props.progress}%;
      max-width: 100%;
      background: ${(props) => props.theme.colors.blue};
      height: 0.1388888889vw;
      display: block;
    }
  }
`

const CartTitleDesktop = styled.div`
  font-size: ${(props) => props.theme.fontSize.m26};
  padding-bottom: ${(props) => props.theme.space.marginMobile}vw;
  padding-left: 0;
  display: none;

  span {
    font-family: ${(props) => props.theme.fontFamily.bold};
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding-left: 0;
    font-size: ${(props) => props.theme.fontSize.d20};
    padding-bottom: 0.4vw;
    display: block;
  }
`

const CloseButton = styled.button`
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    position: relative;
    cursor: pointer;
    height: ${(props) => props.theme.space.marginMobile / 3.5}vw;
    width: ${(props) => props.theme.space.marginMobile / 3.5}vw;

    &::after {
      content: '';
      top: 0;
      height: ${(props) => props.theme.space.marginMobile / 3.5}vw;
      border-left: 2px solid black;
      position: absolute;
      transform: rotate(45deg);
    }

    &::before {
      content: '';
      top: 0;
      height: ${(props) => props.theme.space.marginMobile / 3.5}vw;
      border-left: 2px solid black;
      position: absolute;
      transform: rotate(-45deg);
    }
  }
`

const ProductsList = styled.ul`
  @media (max-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin-top: 4vw;
  }
`

const EmptyCartMessage = styled.div`
  font-size: ${(props) => props.theme.fontSize.m35};
  padding-left: ${(props) => props.theme.space.marginMobile}vw;
  text-align: center;
  margin-top: ${(props) => props.theme.space.marginMobile * 4}vw;
  display: ${(props) => (props.visible ? 'block' : 'none')};

  a {
    margin-top: ${(props) => props.theme.space.marginMobile}vw;
    font-size: ${(props) => props.theme.fontSize.m26};
    display: inline-block;
    font-family: ${(props) => props.theme.fontFamily.medium};
    background: ${(props) => props.theme.colors.blue};
    padding: ${(props) => props.theme.space.marginMobile}vw;
    color: white !important;
    letter-spacing: 0.05em;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d20};
    padding-top: 1.2vw;
    padding-left: 0;
    margin-top: ${(props) => props.theme.space.magrinDesktop * 10}vw;

    a {
      font-size: ${(props) => props.theme.fontSize.d15};
      margin-top: ${(props) => props.theme.space.marginDesktop * 2}vw;
      padding: ${(props) => props.theme.space.marginDesktop * 2}vw
        ${(props) => props.theme.space.marginDesktop * 4}vw;
    }
  }
`

const ProductListItem = styled.li`
  &:not(:first-of-type) {
    padding-top: ${(props) => props.theme.space.marginMobile}vw;

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      padding-top: 0;
    }
  }

  &:first-child {
    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      border-top: 1px solid ${(props) => props.theme.colors.mediumGrey};
    }
  }
`

const BFDiscountMessage = styled.div`
  color: #ff1e00;
  margin-bottom: 4vw;
  font-size: 4vw;
  padding-top: 4vw;
  padding-inline: 4vw;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding: 0;
    padding-top: 1vw;
    margin-bottom: 1vw;
    font-size: 1vw;
  }
`

const Footer = styled.footer`
  position: relative;
  background: white;
  padding: 0 ${(props) => props.theme.space.marginMobile}vw
    ${(props) => props.theme.space.marginMobile}vw;
  display: ${(props) => (props.disabled ? 'none' : 'block')};

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding: 0 0 ${(props) => props.theme.space.marginDesktop * 2}vw;
  }
`

const OrderSummary = styled.div`
  ${({ hasTopBorder, theme }) =>
    hasTopBorder && `border-top: 1px solid ${theme.colors.mediumGrey};`}
  font-size: ${(props) => props.theme.fontSize.m23};
  letter-spacing: 0.05em;
  display: block;
  padding: ${(props) => props.theme.space.marginMobile}vw 0;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
    padding: ${(props) => props.theme.space.marginDesktop * 2}vw 0;
  }
`

const DiscountValuesWrapper = styled.div``

const Row = styled.div`
  width: 100%;
  margin-left: auto;
  display: flex;
  padding-bottom: ${(props) => props.theme.space.marginMobile / 2}vw;
  justify-content: space-between;

  &:last-child {
    padding-bottom: 0;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding-bottom: ${(props) => props.theme.space.marginDesktop}vw;
  }
`

const Key = styled.div`
  text-align: left;
  display: inline-block;
  text-transform: uppercase;
`

const Value = styled.div`
  font-family: ${(props) => props.theme.fontFamily.bold};
  text-align: right;
  display: inline-block;
  color: ${(props) =>
    props.color ? props.color : props.blue ? props.theme.colors.blue : null};
`

const Checkout = styled.a`
  display: flex;
  text-decoration: none;
  color: white;
  font-size: ${(props) => props.theme.fontSize.m26};
  text-align: center;
  background: ${(props) => props.theme.colors.blue};
  height: ${(props) => props.theme.space.marginMobile * 4}vw;
  justify-content: center;
  align-items: center;
  width: 100%;
  letter-spacing: 0.05em;
  cursor: pointer;

  span {
    font-family: ${(props) => props.theme.fontFamily.medium};
  }

  svg {
    width: 1rem;
    height: 1rem;
    position: relative;
    bottom: 0.125rem;
    margin-right: 0.5rem;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    height: ${(props) => props.theme.space.marginDesktop * 6}vw;
    font-size: ${(props) => props.theme.fontSize.d14};
  }
`

const PaymentMethods = styled.ul`
  padding-top: ${(props) => props.theme.space.marginMobile}vw;
  max-width: 55vw;
  margin: auto;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding-top: ${(props) => props.theme.space.marginDesktop * 1.5}vw;
    max-width: 15vw;
    margin: auto;
  }
`

const GermanText = styled.button`
  width: 100%;
  cursor: pointer;
  text-align: center;
  font-size: 3vw;
  margin-top: 3vw;
  color: darkgrey;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: 1vw;
    margin-top: 1vw;
  }
`

export default Cart
